import { LATEST_CATEGORY_LABEL, NO_DATA_DEFAULT_MESSAGE, NO_DATA_HINT, UniverseQuartileChartFrequency, } from './constants';
/**
 * Format the value according to the metric type
 *
 * A negative is parenthesised and coloured rather than signed, which is how the rest of the site
 * writes one. The result is HTML, so it only belongs somewhere that renders it as such.
 *
 * @param value {number | undefined} The value to render.
 * @param metricType {string | null | undefined} The calc's `type`, as the endpoint reports it.
 * @return {string} The formatted value, or an empty string when there is nothing to show.
 */
export const formatValue = (value, metricType) => {
    if (value === undefined || value === null || Number.isNaN(value))
        return '';
    let text;
    const absValue = Math.abs(value).toFixed(2);
    switch (metricType) {
        case 'percent':
            text = `${absValue}%`;
            break;
        case 'figure_money':
            text = `$${absValue}`;
            break;
        default:
            text = absValue;
            break;
    }
    return value < 0 ? `<span class="text-red">(-${text})</span>` : text;
};
/**
 * Format a period's label according to the date and frequency
 *
 * A monthly bucket is a month, a quarterly one a quarter, and a metric with no history has one
 * period that is not a date at all.
 *
 * @param receivedDate {string | number | Date} The period's date, however it arrived.
 * @param frequency {UniverseQuartileChartFrequency | null} How finely the period was bucketed.
 * @return {string} The label for the period.
 */
export const formatDate = (receivedDate, frequency) => {
    let d;
    let formattedDate;
    if (receivedDate instanceof Date) {
        d = receivedDate;
    }
    else if (typeof receivedDate === 'number' && !Number.isNaN(receivedDate)) {
        d = new Date(receivedDate);
    }
    else {
        const parsed = Date.parse(receivedDate);
        d = new Date(parsed);
    }
    switch (frequency) {
        case UniverseQuartileChartFrequency.MONTHLY:
            formattedDate = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
            break;
        case UniverseQuartileChartFrequency.QUARTERLY:
            formattedDate = `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
            break;
        case UniverseQuartileChartFrequency.YEARLY:
            formattedDate = d.getFullYear().toString();
            break;
        default:
            formattedDate = LATEST_CATEGORY_LABEL;
            break;
    }
    return formattedDate;
};
/**
 * One labelled line of a chart tooltip.
 *
 * @param label {string} What the line is naming, usually a series.
 * @param value {string | undefined} The already formatted value; an empty one drops the line.
 * @param bullet {string | null} Optional swatch class, to mark the line with the series' colour.
 * @return {string} The line's markup, or an empty string when there is no value to show.
 */
export const tooltipRow = (label, value, bulletClass = null) => {
    if (!value)
        return '';
    const labelClass = bulletClass ? `chart-legend-item-title ${bulletClass}` : '';
    return `<div class="highcharts-tooltip-item">
    <div class="highcharts-tooltip-item-name"><span class="${labelClass}">${label}</span></div>
    <div class="highcharts-tooltip-item-value">${value}</div>
    </div>`;
};
/** A window bound, written the way a monthly period label is. */
const formatWindowDate = (date) => date ? formatDate(date, UniverseQuartileChartFrequency.MONTHLY) : 'the start of the range';
/**
 * Returns a message for the user explaining why a chart came back empty
 *
 * @param chartData {QuartileChartData | null} The settled payload, or null when nothing loaded.
 * @return {string} A sentence for the empty state.
 */
export const buildNoDataMessage = (chartData) => {
    if (!chartData)
        return NO_DATA_DEFAULT_MESSAGE;
    const { metric, targets, universe, dateRange } = chartData;
    const universeLabel = universe.label || 'this list';
    if (universe.emptyReason === 'no_members') {
        return `${universeLabel} has no funds in it. Pick another universe.`;
    }
    const metricLabel = metric.label || metric.name;
    const funds = `${universe.totalFunds.toLocaleString()} ${universe.totalFunds === 1 ? 'fund' : 'funds'}`;
    // A latest metric is one value as of today, so there is no window to name.
    const period = metric.hasHistory
        ? ` between ${formatWindowDate(dateRange.startDate)} and ${formatWindowDate(dateRange.endDate)}`
        : '';
    const hint = metric.hasHistory ? ` ${NO_DATA_HINT}` : ' Try a different metric or universe.';
    if (targets.some((target) => target.data.length)) {
        return `None of the ${funds} in ${universeLabel} reported ${metricLabel}${period}.${hint}`;
    }
    const targetLabel = targets.length === 1 ? targets[0].name : 'the selected funds';
    return `Neither ${targetLabel} nor the ${funds} in ${universeLabel} reported ${metricLabel}${period}.${hint}`;
};
